/*
 * ============================================================
 * MEWMORA · Unified Profile Store (v2) — Phase 1 data layer
 * ------------------------------------------------------------
 * A versioned, migratable cat-profile record in localStorage.
 * Phase 1 ONLY changes how data is stored/read. It does NOT:
 *   - change any visible UI, quiz questions, or navigation,
 *   - add access gating or hide any price,
 *   - touch the product form, variant id, selling_plan id,
 *     line item properties, cart, or checkout.
 *
 * Compatibility model (Phase 1): the unified store is WRITTEN
 * alongside the existing per-section keys. Those legacy keys
 * remain the source of truth that drives the current UI and the
 * purchase form. Switching reads to prefer this store is a
 * Phase 2 task, kept out of Phase 1 so behavior is unchanged.
 *
 * Never stores price, variant id, or selling plan id.
 * Never uses eval. A corrupt unified key never throws: it is
 * re-migrated from the legacy keys and self-healed (the corrupt
 * value is overwritten with a clean normalized structure), or it
 * falls back to a clean default structure when no legacy data
 * exists. Legacy sessionStorage keys are left intact (their own
 * sections still own them in Phase 1).
 *
 * Clear Profile removes only MEWMORA-owned profile keys: the unified
 * localStorage record plus the related legacy sessionStorage keys
 * (mewmora-play-profile*, mewmora-choose-box*, mewmora-play-passport).
 * It never calls localStorage.clear() or sessionStorage.clear().
 * ============================================================
 */
if (!window.MewmoraProfileStore) {
  (function () {
    'use strict';

    var STORAGE_KEY = 'mewmora.profile.v2';
    var VERSION = 2;

    // Legacy sessionStorage keys (confirmed in the Phase 0 audit).
    var LEGACY_PROFILE_PREFIX = 'mewmora-play-profile'; // { index, answers[], result }
    var LEGACY_CHOOSEBOX_PREFIX = 'mewmora-choose-box'; // { step, plan, fields{...} }
    var LEGACY_PASSPORT_KEY = 'mewmora-play-passport'; // { catName, resultKey, resultSeenAt, seenBox }

    function defaults() {
      return {
        version: VERSION,
        completed: false,
        currentStep: 0,
        catName: '',
        birthdayMonth: '',
        birthdayYear: '',
        catAge: '',
        numberOfCats: '',
        playProfile: '',
        toyPreferences: [],
        catnipPreference: '',
        playEnergy: '',
        breed: '',
        notes: '',
        updatedAt: ''
      };
    }

    function str(v) {
      return typeof v === 'string' ? v : v == null ? '' : String(v);
    }

    function nowIso() {
      try {
        return new Date().toISOString();
      } catch (e) {
        return '';
      }
    }

    // Coerce any parsed object into a fully-defaulted, type-safe profile.
    function normalize(obj) {
      var d = defaults();
      if (!obj || typeof obj !== 'object') return d;

      d.version = VERSION; // always numeric, current
      d.completed = obj.completed === true; // always boolean
      d.currentStep =
        typeof obj.currentStep === 'number' && isFinite(obj.currentStep) ? obj.currentStep : 0; // always number

      d.catName = str(obj.catName);
      d.birthdayMonth = str(obj.birthdayMonth);
      d.birthdayYear = str(obj.birthdayYear);
      d.catAge = str(obj.catAge);
      d.numberOfCats = str(obj.numberOfCats);
      d.playProfile = str(obj.playProfile);
      d.catnipPreference = str(obj.catnipPreference);
      d.playEnergy = str(obj.playEnergy);
      d.breed = str(obj.breed);
      d.notes = str(obj.notes);

      // toyPreferences is ALWAYS an array of non-empty strings.
      if (Array.isArray(obj.toyPreferences)) {
        d.toyPreferences = obj.toyPreferences.filter(function (v) {
          return typeof v === 'string' && v !== '';
        });
      } else if (typeof obj.toyPreferences === 'string' && obj.toyPreferences !== '') {
        d.toyPreferences = [obj.toyPreferences];
      } else {
        d.toyPreferences = [];
      }

      d.updatedAt = str(obj.updatedAt);
      return d;
    }

    function safeParse(raw) {
      if (!raw) return null;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return null;
      }
    }

    function writeRaw(profile) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
        return true;
      } catch (e) {
        return false; // quota / unavailable -> degrade quietly, never touch legacy keys
      }
    }

    // The unified key counts as "present" only when it EXISTS and its JSON
    // parses to an object. Returns that parsed object, or null when the key is
    // absent, unreadable, or corrupt. A single read+parse used by both load()
    // and migrateLegacy() so a corrupt value is detected consistently.
    function readValidNew() {
      try {
        var raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw == null) return null;
        var parsed = safeParse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
      } catch (e) {
        return null; // storage unavailable -> treat as absent, never throw
      }
    }

    /* -------------------- legacy migration (idempotent) -------------------- */
    // Read-only scan of the known legacy sessionStorage keys. Never mutates or
    // deletes them. Returns the fields needed to build a new-structure profile.
    function scanLegacy() {
      var out = { fields: {}, result: '', step: null, catName: '' };
      try {
        for (var i = 0; i < window.sessionStorage.length; i++) {
          var key = window.sessionStorage.key(i);
          if (!key) continue;
          if (key.indexOf(LEGACY_CHOOSEBOX_PREFIX) === 0) {
            var cb = safeParse(window.sessionStorage.getItem(key));
            if (cb) {
              if (cb.fields && typeof cb.fields === 'object') {
                Object.keys(cb.fields).forEach(function (k) {
                  if (out.fields[k] == null || out.fields[k] === '') out.fields[k] = cb.fields[k];
                });
              }
              if (typeof cb.step === 'number' && out.step == null) out.step = cb.step;
            }
          } else if (key.indexOf(LEGACY_PROFILE_PREFIX) === 0) {
            var pp = safeParse(window.sessionStorage.getItem(key));
            if (pp && pp.result && !out.result) out.result = String(pp.result);
          }
        }
        var passport = safeParse(window.sessionStorage.getItem(LEGACY_PASSPORT_KEY));
        if (passport && passport.catName) out.catName = String(passport.catName);
      } catch (e) {
        /* ignore - treat as no legacy data */
      }
      return out;
    }

    // Build a new-structure profile from legacy keys. Never overwrites a valid
    // existing new key (idempotent). A corrupt new key does NOT block migration
    // — it is treated as absent and self-healed by writeRaw() below. Never
    // deletes legacy keys in Phase 1.
    function migrateLegacy() {
      var existing = readValidNew();
      if (existing) return normalize(existing);

      var legacy = scanLegacy();
      var p = defaults();
      var f = legacy.fields || {};

      if (f['Cat Name']) p.catName = str(f['Cat Name']);
      if (f['Cat Age']) p.catAge = str(f['Cat Age']);
      if (f['Number of Cats']) p.numberOfCats = str(f['Number of Cats']);
      if (f['Play Profile']) p.playProfile = str(f['Play Profile']);
      if (f['Catnip Preference']) p.catnipPreference = str(f['Catnip Preference']);
      if (f['Favorite Toy Style']) p.toyPreferences = [str(f['Favorite Toy Style'])];
      if (f['Special Notes']) p.notes = str(f['Special Notes']);

      // Fall back to the quiz result only if Choose Box did not set a value.
      if (!p.playProfile && legacy.result) p.playProfile = legacy.result;
      p.completed = legacy.result ? true : false;
      if (typeof legacy.step === 'number') p.currentStep = legacy.step;
      if (legacy.catName && !p.catName) p.catName = legacy.catName;

      p.updatedAt = nowIso();
      writeRaw(p);
      return p;
    }

    /* -------------------- public API -------------------- */
    function load() {
      try {
        // Valid existing key wins — legacy data can never overwrite it.
        var existing = readValidNew();
        if (existing) return normalize(existing);
        // Absent OR corrupt new key -> migrate from legacy. migrateLegacy()
        // self-heals a corrupt key by writing back a clean normalized
        // structure, and returns clean defaults when no legacy data exists.
        return migrateLegacy();
      } catch (e) {
        return defaults();
      }
    }

    function save(profile) {
      var p = normalize(profile);
      p.version = VERSION;
      p.updatedAt = nowIso();
      writeRaw(p);
      return p;
    }

    // Shallow, whitelisted merge: unknown keys are ignored; defaults filled.
    function update(partial) {
      var current = load();
      if (partial && typeof partial === 'object') {
        var allowed = defaults();
        Object.keys(partial).forEach(function (k) {
          if (Object.prototype.hasOwnProperty.call(allowed, k)) {
            current[k] = partial[k];
          }
        });
      }
      return save(current);
    }

    // Remove the unified record AND only the MEWMORA-owned legacy profile
    // records. Clearing both prevents old cat details from being re-migrated
    // into a later order. Never clear unrelated browser storage.
    function clear() {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        /* ignore */
      }
      try {
        var keys = [];
        for (var i = 0; i < window.sessionStorage.length; i++) {
          var key = window.sessionStorage.key(i);
          if (
            key === LEGACY_PASSPORT_KEY ||
            (key && key.indexOf(LEGACY_PROFILE_PREFIX) === 0) ||
            (key && key.indexOf(LEGACY_CHOOSEBOX_PREFIX) === 0)
          ) {
            keys.push(key);
          }
        }
        keys.forEach(function (key) {
          window.sessionStorage.removeItem(key);
        });
      } catch (e) {
        /* ignore */
      }
    }

    function isComplete() {
      try {
        return load().completed === true;
      } catch (e) {
        return false;
      }
    }

    function getCurrentStep() {
      try {
        return load().currentStep || 0;
      } catch (e) {
        return 0;
      }
    }

    function setCurrentStep(step) {
      var n = typeof step === 'number' && isFinite(step) ? step : 0;
      return update({ currentStep: n });
    }

    // Phase 1 provides the tool only - it does NOT enable any gating.
    function isDesignMode() {
      try {
        if (window.Shopify && window.Shopify.designMode) return true;
      } catch (e) {
        /* ignore */
      }
      try {
        if (document.documentElement.classList.contains('shopify-design-mode')) return true;
      } catch (e) {
        /* ignore */
      }
      return false;
    }

    window.MewmoraProfileStore = {
      STORAGE_KEY: STORAGE_KEY,
      VERSION: VERSION,
      defaults: defaults,
      load: load,
      save: save,
      update: update,
      clear: clear,
      migrateLegacy: migrateLegacy,
      isComplete: isComplete,
      getCurrentStep: getCurrentStep,
      setCurrentStep: setCurrentStep,
      isDesignMode: isDesignMode
    };
  })();
}
